import { setAlgoliaSearchAttribution } from '../../search/algolia/attribution';

const highlightMatch = (html, text, query) => {
  if (!query) return text;

  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return html`${before}<strong class="aa-SuggestionLink__highlight">${match}</strong>${after}`;
};

export const SuggestionLinkTemplate = (html, label, query = '') => html`
  <a
    href=${`/search?q=${encodeURIComponent(label)}`}
    class="aa-ItemLink aa-SuggestionLink"
  >
    <span>${highlightMatch(html, label, query)}</span>
  </a>
`;

export const CollectionLinkTemplate = (html, item, query = '') => {
  const label = item.title || item.name || '';
  return html`
    <a
      href=${`/collections/${item.handle}`}
      class="aa-ItemLink aa-SuggestionLink"
    >
      <span>${highlightMatch(html, label, query)}</span>
    </a>
  `;
};

export const getBlogUrl = (item) => {
  const blogHandle = (item.blog && item.blog.handle) || item.blog_handle;

  if (blogHandle) {
    return `/blogs/${blogHandle}/${item.handle}`;
  }

  return `/blogs/${item.handle}`;
};

export const BlogLinkTemplate = (html, item, query = '') => {
  const label = item.title || '';
  return html`
    <a
      href=${getBlogUrl(item)}
      class="aa-ItemLink aa-SuggestionLink"
    >
      <span>${highlightMatch(html, label, query)}</span>
    </a>
  `;
};

const formatMoney = (value) => {
  if (typeof window !== 'undefined' && typeof window.formattedPrice === 'function') {
    return window.formattedPrice(value);
  }

  return '';
};

const getCustomerDiscountValue = (tags) => {
  if (typeof window !== 'undefined' && typeof window.customerDiscount === 'function') {
    return Number(window.customerDiscount(tags)) || 0;
  }

  return 0;
};

const getDiscountedPrice = (priceValue, compareAtPriceValue, customerDiscountValue) => {
  const price = Number(priceValue);
  const compareAtPrice = Number(compareAtPriceValue);

  if (!Number.isFinite(price)) {
    return 0;
  }

  if (Number.isFinite(compareAtPrice) && compareAtPrice > 0) {
    return Math.min(compareAtPrice * (1 - customerDiscountValue), price);
  }

  return price * (1 - customerDiscountValue);
};

const getDisplayComparePrice = (priceValue, compareAtPriceValue) => {
  const price = Number(priceValue);
  const compareAtPrice = Number(compareAtPriceValue);

  if (Number.isFinite(compareAtPrice) && compareAtPrice > 0) {
    return compareAtPrice;
  }

  return Number.isFinite(price) ? price : 0;
};

const getProductUrl = (item) => item.url || `/products/${item.handle}`;

const getProductImage = (item) => {
  const defaultSwatch = getDefaultSwatch(item);
  const featuredImage = item.featured_image;

  return (defaultSwatch && defaultSwatch.image) || (defaultSwatch && defaultSwatch.hover_image) || item.image || item.hover_image || (featuredImage && featuredImage.url) || featuredImage || item.thumbnail || '';
};

const getProductHoverImage = (item) => {
  const defaultSwatch = getDefaultSwatch(item);

  return (defaultSwatch && defaultSwatch.hover_image) || (defaultSwatch && defaultSwatch.image) || item.hover_image || '';
};

const getSwatchColorKey = (swatch) => {
  if (!swatch || typeof swatch.color !== 'string') {
    return '';
  }

  return swatch.color.trim().toLowerCase();
};

const mergeSwatchGroup = (primary, candidate) => {
  const preferred = primary.available ? primary : candidate.available ? candidate : primary;

  return {
    ...preferred,
    color: preferred.color || primary.color || candidate.color || '',
    available: Boolean(primary.available || candidate.available),
    price: preferred.price || primary.price || candidate.price,
    compare_at_price: preferred.compare_at_price || primary.compare_at_price || candidate.compare_at_price,
    image: preferred.image || primary.image || candidate.image,
    hover_image: preferred.hover_image || primary.hover_image || candidate.hover_image,
    swatch_image: preferred.swatch_image || primary.swatch_image || candidate.swatch_image,
    variant_id: preferred.variant_id || primary.variant_id || candidate.variant_id,
  };
};

const getProductSwatches = (item) => {
  if (!Array.isArray(item.swatches)) {
    return [];
  }

  return item.swatches
    .filter(Boolean)
    .reduce((mergedSwatches, swatch) => {
      const colorKey = getSwatchColorKey(swatch);

      if (!colorKey) {
        mergedSwatches.push(swatch);
        return mergedSwatches;
      }

      const existingIndex = mergedSwatches.findIndex((entry) => getSwatchColorKey(entry) === colorKey);

      if (existingIndex === -1) {
        mergedSwatches.push(swatch);
        return mergedSwatches;
      }

      mergedSwatches[existingIndex] = mergeSwatchGroup(mergedSwatches[existingIndex], swatch);
      return mergedSwatches;
    }, [])
    .filter((swatch) => swatch.available);
};

const getDefaultSwatch = (item) => {
  const swatches = getProductSwatches(item);

  return swatches[0];
};

const isGiftCard = (item) => {
  if (item.gift_card === true) return true;
  if (typeof item.product_type === 'string' && item.product_type.toLowerCase().includes('gift card')) return true;
  if (Array.isArray(item.tags) && item.tags.some((t) => typeof t === 'string' && t.toLowerCase().includes('gift card'))) return true;
  return false;
};

const getProductTitle = (item) => item.title || item.name || '';

const getSwatchPrimaryImage = (swatch, fallbackImage) => swatch.image || swatch.hover_image || fallbackImage || '';

const getSwatchHoverImage = (swatch, fallbackHoverImage, fallbackImage) => (
  swatch.hover_image || swatch.image || fallbackHoverImage || fallbackImage || ''
);

const updateProductCardImages = (card, swatchButton) => {
  if (!card || !swatchButton) {
    return;
  }

  const primaryImage = card.querySelector('.aa-ProductCard__image-primary');
  const hoverImage = card.querySelector('.aa-ProductCard__image-hover');
  const nextPrimarySrc = swatchButton.dataset.image || '';
  const nextHoverSrc = swatchButton.dataset.hoverImage || '';
  const nextAlt = swatchButton.dataset.color || card.dataset.title || '';
  const nextPrice = swatchButton.dataset.price || '';
  const nextComparePrice = swatchButton.dataset.comparePrice || '';

  if (primaryImage && nextPrimarySrc) {
    primaryImage.src = nextPrimarySrc;
    primaryImage.alt = nextAlt;
  }

  if (hoverImage) {
    if (nextHoverSrc && nextHoverSrc !== nextPrimarySrc) {
      hoverImage.src = nextHoverSrc;
      hoverImage.hidden = false;
    } else {
      hoverImage.hidden = true;
      hoverImage.removeAttribute('src');
    }
  }

  card.querySelectorAll('.aa-ProductCard__swatch').forEach((button) => {
    button.classList.toggle('is-active', button === swatchButton);
    button.setAttribute('aria-pressed', button === swatchButton ? 'true' : 'false');
  });

  const priceEl = card.querySelector('.aa-ProductCard__price');
  const comparePriceEl = card.querySelector('.aa-ProductCard__compare-price');

  if (priceEl && nextPrice) {
    priceEl.textContent = nextPrice;
    priceEl.classList.toggle('aa-ProductCard__price--sale', Boolean(nextComparePrice));
  }

  if (comparePriceEl) {
    if (nextComparePrice) {
      comparePriceEl.textContent = nextComparePrice;
      comparePriceEl.hidden = false;
    } else {
      comparePriceEl.hidden = true;
    }
  }
};

const createCardClickHandler = (item, productUrl) => (event) => {
  const interactiveTarget = event.target.closest('button, a');

  if (interactiveTarget && !interactiveTarget.classList.contains('aa-ProductCard__card-link')) {
    return;
  }

  setAlgoliaSearchAttribution(item);
  window.location.href = productUrl;
};

export const ProductCardTemplate = (html, item) => {
  const customerDiscountValue = getCustomerDiscountValue(item.tags);
  const price = getDiscountedPrice(item.price, item.compare_at_price, customerDiscountValue);
  const compareAtPrice = getDisplayComparePrice(item.price, item.compare_at_price);
  const hasComparePrice = Number.isFinite(compareAtPrice) && Number.isFinite(price) && compareAtPrice > price;
  const productUrl = getProductUrl(item);
  const image = getProductImage(item);
  const hoverImage = getProductHoverImage(item);
  const swatches = getProductSwatches(item);
  const activeSwatch = getDefaultSwatch(item);
  const title = getProductTitle(item);

  return html`
    <article
      class="aa-ProductCard"
      data-title=${title}
      onClick=${createCardClickHandler(item, productUrl)}
    >
      <a
        href=${productUrl}
        class="aa-ProductCard__card-link"
        aria-label=${title}
      >
        <div class="aa-ProductCard__image">
          ${image ? html`<img class="aa-ProductCard__image-primary" src=${image} alt=${title} />` : null}
          ${hoverImage && hoverImage !== image
    ? html`<img class="aa-ProductCard__image-hover" src=${hoverImage} alt="" aria-hidden="true" />`
    : html`<img class="aa-ProductCard__image-hover" alt="" aria-hidden="true" hidden />`}
        </div>
      </a>
      ${swatches.length && !isGiftCard(item) ? html`
        <div class="aa-ProductCard__swatches" aria-label="Available colors">
          ${swatches.map((swatch) => {
    const swatchImage = getSwatchPrimaryImage(swatch, image);
    const swatchHoverImage = getSwatchHoverImage(swatch, hoverImage, swatchImage);
    const isActive = activeSwatch && getSwatchColorKey(activeSwatch) === getSwatchColorKey(swatch);
    const swatchPrice = getDiscountedPrice(swatch.price || item.price, swatch.compare_at_price || item.compare_at_price, customerDiscountValue);
    const swatchComparePrice = getDisplayComparePrice(swatch.price || item.price, swatch.compare_at_price || item.compare_at_price);
    const swatchHasComparePrice = Number.isFinite(swatchComparePrice) && Number.isFinite(swatchPrice) && swatchComparePrice > swatchPrice;

    return html`
              <button
                type="button"
                class=${`aa-ProductCard__swatch${swatch.available ? '' : ' aa-ProductCard__swatch--unavailable'}${isActive ? ' is-active' : ''}`}
                title=${swatch.color || ''}
                aria-label=${`${swatch.color || 'Color'}${swatch.available ? '' : ' unavailable'}`}
                aria-pressed=${isActive ? 'true' : 'false'}
                data-color=${swatch.color || ''}
                data-image=${swatchImage}
                data-hover-image=${swatchHoverImage}
                data-price=${formatMoney(swatchPrice)}
                data-compare-price=${swatchHasComparePrice ? formatMoney(swatchComparePrice) : ''}
                onMouseDown=${(event) => {
      event.preventDefault();
      event.stopPropagation();
    }}
                onClick=${(event) => {
      event.preventDefault();
      event.stopPropagation();
      updateProductCardImages(event.currentTarget.closest('.aa-ProductCard'), event.currentTarget);
    }}
              >
                ${swatch.swatch_image
      ? html`<img class="aa-ProductCard__swatch-image" src=${swatch.swatch_image} alt=${swatch.color || ''} />`
      : html`<span class="aa-ProductCard__swatch-fallback" aria-hidden="true"></span>`}
              </button>
            `;
  })}
        </div>
      ` : null}
      <a
        href=${productUrl}
        class="aa-ProductCard__card-link aa-ProductCard__content-link"
      >
        <div class="aa-ProductCard__info">
          <div class="aa-ProductCard__title">${title}</div>
          <div class="aa-ProductCard__price-wrapper">
            <div class=${`aa-ProductCard__price${hasComparePrice ? ' aa-ProductCard__price--sale' : ''}`}>${formatMoney(price)}</div>
            <div class="aa-ProductCard__compare-price" hidden=${!hasComparePrice}>${hasComparePrice ? formatMoney(compareAtPrice) : ''}</div>
          </div>
        </div>
      </a>
    </article>
  `;
};
